import React from "react";
import requiresAuth from "./requiresAuth";
import isProdReady from "./isProdReady";
import requiresClient from "./requiresClient";
import bindActionCreators from "../../utils/bindActionCreators";
import { USER_AUTH_KEY } from "../../utils/constants";
import jwtDecode from "jwt-decode";

import {
  clearSession,
  createSession,
  preventNextRedirect,
  SET_USER,
  SET_ERROR,
  setSelectedClient,
} from "./authActions";
import authReducer from "./authReducer";
import { AuthService } from "./authService";
import fetchMsEmailUser from "./fetchMsEmailUser";
import "./awsExport";
import { Auth } from "aws-amplify";

export const AuthStateContext = React.createContext();
export const AuthDispatchContext = React.createContext();

export const useAuthState = () => {
  const context = React.useContext(AuthStateContext);
  if (!context) {
    throw new Error("useAuthState must be used within an AuthProvider");
  }
  return context;
};

export const useAuthDispatch = () => {
  const dispatch = React.useContext(AuthDispatchContext);
  if (!dispatch) {
    throw new Error("useAuthDispatch must be used within an AuthProvider");
  }

  const state = useAuthState();
  const getState = () => state;

  return bindActionCreators(
    {
      clearSession,
      createSession,
      preventNextRedirect,
      setSelectedClient,
    },
    dispatch,
    getState,
  );
};

export const useAuth = () => [useAuthState(), useAuthDispatch()];

const AuthProvider = ({ children = null }) => {
  const sessionUser = window.localStorage.getItem(USER_AUTH_KEY);
  const user = sessionUser ? JSON.parse(sessionUser) : null;
  const initialState = {
    error: null,
    isAuthenticated: user !== null,
    isCreatingSession: false,
    user,
    redirectRoute: null,
  };

  const [state, dispatch] = React.useReducer(authReducer, initialState);
  const [isCheckingSession, setIsCheckingSession] = React.useState(false);

  // Check for Cognito session and fetch user if needed
  React.useEffect(() => {
    async function checkAndFetchUser() {
      // Skip if we already have a user or if we're already checking
      if (state.user || isCheckingSession) {
        return;
      }

      try {
        setIsCheckingSession(true);
        const session = await Auth.currentSession();

        // ✅ CORRECT: getIdToken() returns CognitoIdToken object, then call getJwtToken()
        const idTokenString = session.getIdToken().getJwtToken();

        if (idTokenString) {
          // ✅ Decode the JWT string
          const decodedToken = jwtDecode(idTokenString);
          const user = await fetchMsEmailUser(decodedToken);

          if (user) {
            dispatch({ type: SET_USER, user });
            window.localStorage.setItem(USER_AUTH_KEY, JSON.stringify(user));
          }
        }
      } catch (error) {
        // ✅ FIXED - Better error handling
        if (
          error === "No current user" ||
          error.message === "No current user" ||
          error === "The user is not authenticated"
        ) {
          console.log("No authenticated user found");
        } else {
          console.error("❌ Error in AuthProvider:", error);
          dispatch({ type: SET_ERROR, error: error.message });
        }
      } finally {
        setIsCheckingSession(false);
      }
    }

    checkAndFetchUser();
  }, []); // Run only once on mount

  // Update localStorage when user changes
  React.useEffect(() => {
    if (state.user) {
      window.localStorage.setItem(USER_AUTH_KEY, JSON.stringify(state.user));
    }
  }, [state.user]);

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export { AuthProvider as default, requiresAuth, requiresClient, isProdReady };
